import { supabase } from '../lib/supabase';

/**
 * Service for managing workflow node library
 * Handles CRUD operations for both system and user-created nodes
 */
export const nodeLibraryService = {
  /**
   * Get all nodes (system + user's custom nodes)
   * @returns {Promise<Array>} Array of node objects
   */
  async getAllNodes() {
    try {
      const { data, error } = await supabase?.from('workflow_nodes')?.select('*')?.order('category', { ascending: true })?.order('name', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(node => ({
        id: node?.node_id,
        name: node?.name,
        category: node?.category,
        categoryName: node?.category_name,
        icon: node?.icon,
        description: node?.description,
        color: node?.color,
        shortcut: node?.shortcut,
        node_type: node?.node_type,
        isSystem: node?.is_system,
        userId: node?.user_id,
        createdAt: node?.created_at,
        updatedAt: node?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching nodes:', error);
      throw new Error(error?.message || 'Failed to fetch workflow nodes');
    }
  },

  /**
   * Get nodes by category
   * @param {string} category - Category to filter by
   * @returns {Promise<Array>} Array of node objects
   */
  async getNodesByCategory(category) {
    try {
      const { data, error } = await supabase?.from('workflow_nodes')?.select('*')?.eq('category', category)?.order('name', { ascending: true });

      if (error) throw error;

      return data?.map(node => ({
        id: node?.node_id,
        name: node?.name,
        category: node?.category,
        categoryName: node?.category_name,
        icon: node?.icon,
        description: node?.description,
        color: node?.color,
        shortcut: node?.shortcut,
        node_type: node?.node_type,
        isSystem: node?.is_system,
        userId: node?.user_id
      })) || [];
    } catch (error) {
      console.error('Error fetching nodes by category:', error);
      throw new Error(error?.message || 'Failed to fetch nodes by category');
    }
  },

  /**
   * Create a new custom node
   * @param {Object} nodeData - Node data (name, category, icon, etc.)
   * @returns {Promise<Object>} Created node object
   */
  async createNode(nodeData) {
    try {
      // Validate required fields
      if (!nodeData?.category) {
        throw new Error('Category is required');
      }
      if (!nodeData?.name) {
        throw new Error('Name is required');
      }

      // Get user if authenticated, allow null for unauthenticated
      const { data: { user } } = await supabase?.auth?.getUser();

      // Generate unique node_id
      const nodeId = `custom-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;

      const { data, error } = await supabase?.from('workflow_nodes')?.insert({
          node_id: nodeId,
          name: nodeData?.name,
          category: nodeData?.category,
          category_name: nodeData?.categoryName,
          icon: nodeData?.icon,
          description: nodeData?.description,
          color: nodeData?.color,
          shortcut: nodeData?.shortcut,
          node_type: nodeData?.nodeType || 'task',
          user_id: user?.id || null,
          is_system: false
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.node_id,
        name: data?.name,
        category: data?.category,
        categoryName: data?.category_name,
        icon: data?.icon,
        description: data?.description,
        color: data?.color,
        shortcut: data?.shortcut,
        node_type: data?.node_type,
        isSystem: data?.is_system,
        userId: data?.user_id
      };
    } catch (error) {
      console.error('Error creating node:', error);
      throw new Error(error?.message || 'Failed to create node');
    }
  },

  /**
   * Update an existing custom node
   * @param {string} nodeId - Node ID to update
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated node object
   */
  async updateNode(nodeId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Build update query based on authentication status
      let query = supabase?.from('workflow_nodes')?.update({
          name: updates?.name,
          category: updates?.category,
          category_name: updates?.categoryName,
          icon: updates?.icon,
          description: updates?.description,
          color: updates?.color,
          shortcut: updates?.shortcut,
          node_type: updates?.nodeType
        })?.eq('node_id', nodeId);

      // If user is authenticated, match their user_id
      // If not authenticated, match null user_id
      if (user) {
        query = query?.eq('user_id', user?.id);
      } else {
        query = query?.is('user_id', null);
      }

      // Execute query with select and single
      const { data, error } = await query?.select()?.single();

      if (error) throw error;

      return {
        id: data?.node_id,
        name: data?.name,
        category: data?.category,
        categoryName: data?.category_name,
        icon: data?.icon,
        description: data?.description,
        color: data?.color,
        shortcut: data?.shortcut,
        node_type: data?.node_type,
        isSystem: data?.is_system,
        userId: data?.user_id
      };
    } catch (error) {
      console.error('Error updating node:', error);
      throw new Error(error?.message || 'Failed to update node');
    }
  },

  /**
   * Delete a custom node
   * @param {string} nodeId - Node ID to delete
   * @returns {Promise<void>}
   */
  async deleteNode(nodeId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Build delete query based on authentication status
      let query = supabase?.from('workflow_nodes')?.delete()?.eq('node_id', nodeId);

      // If user is authenticated, match their user_id
      // If not authenticated, match null user_id
      if (user) {
        query = query?.eq('user_id', user?.id);
      } else {
        query = query?.is('user_id', null);
      }

      const { error } = await query;

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting node:', error);
      throw new Error(error?.message || 'Failed to delete node');
    }
  },

  /**
   * Delete all custom nodes (non-system nodes)
   * @returns {Promise<void>}
   */
  async deleteAllNodes() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Build delete query based on authentication status
      let query = supabase?.from('workflow_nodes')?.delete()?.neq('node_id', '');

      // If user is authenticated, match their user_id
      // If not authenticated, match null user_id
      if (user) {
        query = query?.eq('user_id', user?.id);
      } else {
        query = query?.is('user_id', null);
      }

      const { error } = await query;

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting all nodes:', error);
      throw new Error(error?.message || 'Failed to delete all nodes');
    }
  },

  /**
   * Delete ALL nodes including system nodes
   * @returns {Promise<void>}
   */
  async deleteAllNodesIncludingSystem() {
    try {
      // Delete ALL nodes without any filters
      const { error } = await supabase?.from('workflow_nodes')?.delete()?.neq('node_id', '');

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting all nodes including system:', error);
      throw new Error(error?.message || 'Failed to delete all nodes');
    }
  }
};

export default nodeLibraryService;